import './PodPage.css';

import { useEffect, useRef, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import Disclaimer from '../components/Disclaimer';
import mixpanel from 'mixpanel-browser';
import Faq from '../components/Faq';
import Finished from '../components/Finished';
import Home from '../components/Home';
import Meditation from '../components/meditation/Meditation';
import PostFeeling from '../components/PostFeeling';
import PreFeeling from '../components/PreFeeling';
import SelectAudio from '../components/SelectAudio';
import SelectScene from '../components/SelectScene';
import { Screen, usePodContext } from '../PodContext';

const screenOrder: Record<string, number> = {
  [Screen.HOME]: 0,
  [Screen.DISCLAIMER]: 1,
  [Screen.PRE_FEELING]: 2,
  [Screen.SELECT_SCENE]: 3,
  [Screen.SELECT_AUDIO]: 4,
  [Screen.MEDITATION]: 5,
  [Screen.POST_FEELING]: 6,
  [Screen.FINISHED]: 7,
  [Screen.FAQ]: -1,
};

const PodPage = () => {
  const location = useLocation();
  const podContext = usePodContext();

  // Estado para transições
  const [currentLocation, setCurrentLocation] = useState(location);
  const [prevLocation, setPrevLocation] = useState(location);
  const [incomingLocation, setIncomingLocation] = useState(location);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const transitionTimer = useRef<number | null>(null);
  const ANIM_MS = 500;


  useEffect(() => {
    if (location.pathname === currentLocation.pathname) return;
    if (isTransitioning) return; 

    const from = screenOrder[currentLocation.pathname] ?? 0;
    const to = screenOrder[location.pathname] ?? 0;
    setDirection(to > from ? 'forward' : 'backward');

    setPrevLocation(currentLocation);
    setIncomingLocation(location);
    setIsTransitioning(true);

    if (transitionTimer.current) window.clearTimeout(transitionTimer.current);
    transitionTimer.current = window.setTimeout(() => {
      setCurrentLocation(location);
      setIsTransitioning(false);
      transitionTimer.current = null;
    }, ANIM_MS);
  }, [location, currentLocation, isTransitioning]);

  useEffect(() => {
    return () => {
      if (transitionTimer.current) window.clearTimeout(transitionTimer.current);
    };
  }, []);

  useEffect(() => {
    mixpanel.track_pageview({
      screen: location.pathname,
      audio: podContext.audio?.name,
      postFeeling: podContext.postFeeling,
      preFeeling: podContext.preFeeling,
      scene: podContext.scene?.title,
    });
  }, [location, podContext]);

  const renderRoutesAt = (loc: typeof location, phase: 'show' | 'exiting' | 'entering') => (
    <Routes location={loc}>
      <Route path={Screen.HOME} element={<Home />} />
      <Route path={Screen.DISCLAIMER} element={<Disclaimer />} />
      <Route path={Screen.PRE_FEELING} element={<PreFeeling />} />
      <Route path={Screen.SELECT_SCENE} element={<SelectScene />} />
      <Route path={Screen.SELECT_AUDIO} element={<SelectAudio />} />
      <Route
        path={Screen.MEDITATION}
          element={
            phase === 'entering'
              ? <Meditation key={`med-${loc.key || loc.pathname}`} />
              : <Meditation />
        }
      />
      <Route path={Screen.POST_FEELING} element={<PostFeeling />} />
      <Route path={Screen.FINISHED} element={<Finished />} />
      <Route path={Screen.FAQ} element={<Faq />} />
    </Routes>
  );

  return (
    <div className={`pod-container${isTransitioning ? ' is-transitioning' : ''}`}>
      {isTransitioning ? (
        <>
          <div className={`pod-route-layer exiting ${direction}`}>
            {renderRoutesAt(prevLocation, 'exiting')}
          </div>
          <div className={`pod-route-layer entering ${direction}`}>
            {renderRoutesAt(incomingLocation, 'entering')}
          </div>
        </>
      ) : (
        <div className="pod-route-layer show">
          {renderRoutesAt(currentLocation, 'show')}
        </div>
      )}
    </div>
  );
}

export default PodPage